import type { Uuid } from "@/domains/global/identifiers";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import type { DesktopRecordingStartAttemptTrace } from "@/modules/electron/types";
import type {
  TranscriptSpeakerIdentificationContext,
  TranscriptSpeakerIdentificationReason,
  TranscriptSpeakerAudioSource,
  TranscriptSpeakerParticipantMetadata,
  TranscriptSpeakerVariant,
} from "@/modules/audio-recording/transcript-types";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import type { RecordingWindow } from "@/store/audio/recording/recording-window";
import type { DesktopControllerKind } from "@/store/audio/recording/media-controller/desktop-controller-errors";

export interface IAudioRecordingStore {
  start(args: {
    preparedRecording: PreparedRecording;
    recordingWindow?: RecordingWindow;
    meetingEventWindowId?: string;
  }): Promise<void>;
  pause(args: { audioRecordingId: Uuid }): Promise<void>;
  resume(args: { audioRecordingId: Uuid }): Promise<void>;
  stop(args: { audioRecordingId: Uuid }): Promise<void>;
  clear(args: { audioRecordingId: Uuid }): Promise<void>;
}

export enum MediaRecorderMimeType {
  WebmOpus = "audio/webm;codecs=opus",
  Webm = "audio/webm",
  OggOpus = "audio/ogg;codecs=opus",
  Pcm16 = "audio/pcm;codecs=pcm16",
}

export type MediaRecorderAudioChunk = {
  blob: Blob;
  durationMs: number;
  mimeType: MediaRecorderMimeType;
  byteLength: number;
};

export type MediaRecorderTranscriptTurn = {
  channel: 0 | 1;
  transcript: string;
  timestampMs: number;
  isEndOfTurn?: boolean;
  participantId?: number | string | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  speakerId?: string | null;
  speakerVariant?: TranscriptSpeakerVariant | null;
  speakerIdentificationContext?: TranscriptSpeakerIdentificationContext | null;
  speakerIdentificationReason?: TranscriptSpeakerIdentificationReason | null;
  speakerAudioSource?: TranscriptSpeakerAudioSource | null;
  words?: Array<{
    word: string;
    startMs: number;
    endMs: number;
  }>;
  raw?: unknown;
  turnOrder: number;
};

export type MediaRecorderTranscriptPartialTurn = MediaRecorderTranscriptTurn & {
  isEndOfTurn: false;
};

export type MediaRecorderTranscriptFinalTurn = MediaRecorderTranscriptTurn & {
  isEndOfTurn: true;
};

export enum TranscriptionStatus {
  Live = "live",
  Disconnected = "disconnected",
  Compromised = "compromised",
}

export type MediaRecorderWaveformSamples = {
  sampleRate: number;
  pcm: Float32Array | Int16Array;
  elapsedDurationMs?: number;
};

export type MediaRecorderCapabilities = {
  audio: true;
  transcripts: boolean;
  waveform: boolean;
  pauseResume: boolean;
};

export type MediaRecorderEventHandlers = {
  onStart?: () => void;
  onStop?: () => void;
  onError?: ({ error }: { error: unknown }) => void;
  onAudioChunk?: ({ chunk }: { chunk: MediaRecorderAudioChunk }) => void;
  onWaveformSamples?: ({ samples }: { samples: MediaRecorderWaveformSamples }) => void;
};

export type MediaTranscriberEventHandlers = {
  onStart?: () => void;
  onStop?: () => void;
  onError?: ({ error }: { error: unknown }) => void;
  onTranscriptPartial?: ({
    partialTurn,
  }: {
    partialTurn: MediaRecorderTranscriptPartialTurn;
  }) => void;
  onTranscriptFinal?: ({ finalTurn }: { finalTurn: MediaRecorderTranscriptFinalTurn }) => void;
  onStatusChange?: ({ status }: { status: TranscriptionStatus }) => void;
};

export interface MediaRecorderEngine {
  setEventHandlers(handlers: MediaRecorderEventHandlers): void;
  start(): Promise<void>;
  stop(): Promise<void>;
  dispose(): void;
  pause(): Promise<void>;
  resume(): Promise<void>;
}

export interface MediaTranscriberEngine {
  setEventHandlers(handlers: MediaTranscriberEventHandlers): void;
  start(): Promise<void>;
  stop(): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  dispose(): void;
}

export type MediaControllerEventHandlers = {
  onStart?: () => void;
  onStop?: () => void;
  onError?: ({ error }: { error: unknown }) => void;
  onAudioChunk?: ({ chunk }: { chunk: MediaRecorderAudioChunk }) => void;
  onTranscriptPartial?: ({
    partialTurn,
  }: {
    partialTurn: MediaRecorderTranscriptPartialTurn;
  }) => void;
  onTranscriptFinal?: ({ finalTurn }: { finalTurn: MediaRecorderTranscriptFinalTurn }) => void;
  onWaveformSamples?: ({ samples }: { samples: MediaRecorderWaveformSamples }) => void;
  onTranscriptionUnavailable?: () => void;
  onTranscriptionUnsupported?: () => void;
  onTranscriptionStatusChange?: ({ status }: { status: TranscriptionStatus }) => void;
  onVoiceDisconnected?: () => void;
  onVoiceReconnected?: () => void;
  onDebugEvent?: ({ data, kind }: { data: Record<string, unknown>; kind: string }) => void;
};

export type MediaControllerInitializeOptions = {
  audioRecordingId: Uuid;
  calendarEventId?: string;
  noteId: Uuid | null;
  meetingEventWindowId?: string;
  /** Which window the Recall SDK records; defaults to whole-desktop.
   *  Web controllers ignore it. */
  recordingWindow?: RecordingWindow;
  replacePreviousAudioRecordingId?: Uuid;
  initializationTimeoutMs?: number;
  startAttemptTrace?: DesktopRecordingStartAttemptTrace;
  onBeginRecording?: () => void;
  onFallbackToWebMedia?: ({ error }: { error: unknown }) => Promise<void> | void;
};

export type MediaControllerKind = "web_media_controller" | DesktopControllerKind;

export interface MediaController {
  readonly kind: MediaControllerKind;
  initializeAndStart(options: MediaControllerInitializeOptions): Promise<void>;
  initialize(options: MediaControllerInitializeOptions): Promise<void>;
  initializeForRecoveredSession?(args: { audioRecordingId: Uuid }): Promise<void>;
  start(): Promise<void>;
  stop(): Promise<void>;
  detachAfterExternalStop?(): Promise<void> | void;
  pause(): Promise<void>;
  resume(): Promise<void>;
  getVisualizer(): AudioLevelProbe | null;
  dispose(): void;
}
